'use client';

import React, {
  ComponentType,
  CSSProperties,
} from 'react';
import clsx from 'clsx';
import {
  useRouter,
} from 'next/navigation';
import ToolTip from '@/core/components/Tooltip';
import {
  ChevronRight,
} from 'lucide-react';
import ClearIcon from '@/core/icons/clear';
import WarningIcon from '@/core/icons/warning';
import PendingIcon from '@/core/icons/pending';
import CriticalIcon from '@/core/icons/critical';
import Stepper from '@mui/material/Stepper';
import Connector, {
  stepConnectorClasses,
} from '@mui/material/StepConnector';
import {
  styled,
  SxProps,
  Theme,
} from '@mui/material/styles';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import {
  StepIconProps,
  SvgIconProps,
} from '@mui/material';

export type PhaseRisk = 'Clear' | 'Warning' | 'Pending' | 'Critical' | null | undefined;

type StrictRisk = 'Clear' | 'Warning' | 'Pending' | 'Critical';

export const PHASE_COLORS = Object.freeze({
  Clear: 'text-brand',
  Warning: 'text-amber-400',
  Critical: 'text-red-500',
  Pending: 'text-slate-300',
});

const CONNECTOR_HEX_BY_PREV: Record<StrictRisk, string> = {
  Clear: '#70c48f',
  Warning: '#fbbf24',
  Critical: '#ef4444',
  Pending: '#e6e4e4',
};

const RISK_ICON: Record<StrictRisk, ComponentType<SvgIconProps>> = {
  Clear: ClearIcon,
  Warning: WarningIcon,
  Critical: CriticalIcon,
  Pending: PendingIcon,
};

type PhaseStepIconProps = StepIconProps & {
  step: PhaseStep;
  onStepClick: (s: PhaseStep) => void;
  classes?: Props['classes'];
  active?: boolean;
};

function PhaseStepIcon({
  step,
  onStepClick,
  classes,
  active,
}: PhaseStepIconProps) {
  const Icon = RISK_ICON[step.risk as StrictRisk];
  return (
    <ToolTip title={`Open ${step.label} checklist`} placement="top">
      <button
        type="button"
        onClick={() => {
          onStepClick(step);
        }}
        className={clsx(
          'group/step flex flex-col items-center gap-1 outline-none z-50',
        )}
      >
        <span
          className={clsx(
            'relative inline-flex items-center justify-center w-6 h-6 rounded-full transition-colors',
            'border-0 shadow-none outline-none',
            'bg-white',
            'group-hover/step:border group-hover/step:border-blue-600 group-hover/step:border-solid group-hover/step:bg-white',
            classes?.iconRoot,
          )}
        >
          {/* Default icon by risk/state (hidden when hovering this button) */}
          <span className={clsx('transition-opacity', 'group-hover/step:opacity-0')}>
            <Icon
              className={clsx('w-4 h-4', PHASE_COLORS[step.risk as StrictRisk], classes?.icon)}
            />
          </span>
          {/* Hover icon (chevron) overrides default for this button only */}
          <ChevronRight
            className={clsx(
              'w-4 h-4 absolute inset-0 m-auto transition-opacity',
              'opacity-0 group-hover/step:opacity-100',
              'text-blue-600 group-hover/step:text-blue-700',
              classes?.icon,
            )}
            strokeWidth={1}
          />
        </span>
        <span
          className={clsx(
            'text-xs transition-colors whitespace-nowrap group-hover/step:text-blue-600 !font-semibold',
            { [PHASE_COLORS.Clear]: step.risk === 'Clear' },
            { [PHASE_COLORS.Warning]: step.risk === 'Warning' },
            { [PHASE_COLORS.Critical]: step.risk === 'Critical' },
            { 'text-slate-600': step.risk === 'Pending' || !step.risk },
            { 'border-b-2 border-current': active },
            classes?.label,
          )}
        >
          {step.label}
        </span>
      </button>
    </ToolTip>
  );
}

export type PhaseStep = {
  key: string;
  label: string;
  risk?: PhaseRisk; // risk drives visuals (icons, connector colors). 'Clear' implies "done".
};

type Props = {
  steps: PhaseStep[];
  checklistId?: number;
  activePhaseKey?: string;
  classes?: {
    root?: string;
    label?: string;
    icon?: string;
    iconRoot?: string;
  };
  sxProps?: SxProps<Theme>;
  connectorTopPx?: number;
};

const PhaseConnector = styled(Connector)(({ theme }) => ({
  [`&.${stepConnectorClasses.alternativeLabel}`]: {
    top: 'var(--step-connector-top, 10px)',
    left: 'calc(-50%)',
    right: 'calc(50%)',
  },
  [`&.${stepConnectorClasses.active}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      borderColor: 'var(--connector-color, #e5e7eb)',
    },
  },
  [`&.${stepConnectorClasses.completed}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      borderColor: 'var(--connector-color, #e5e7eb)',
    },
  },
  [`& .${stepConnectorClasses.line}`]: {
    borderColor: 'var(--connector-color, #e5e7eb)',
    borderTopWidth: 1,
    ...theme.applyStyles('dark', {
      borderColor: 'var(--connector-color, #374151)',
    }),
  },
}));

/**
 * PhaseStepper
 * Horizontal stepper with connectors, matching the design shown:
 * - done: green connector and check circle
 * - active: blue outlined circle with chevron, blue label
 * - upcoming: gray circle; on hover → blue outline + chevron + blue label
 */
export default function PhaseStepper({ steps, checklistId, classes, activePhaseKey, sxProps, connectorTopPx }: Props) {
  const router = useRouter();
  const onClick = (step: PhaseStep) => {
    if (checklistId && checklistId > 0) {
      const phase = step?.key ?? 'Scheduling';
      router.push(`/checklist?id=${encodeURIComponent(String(checklistId))}&phase=${encodeURIComponent(phase)}`);
    }
  };

  return (
    <div className={clsx('flex flex-col items-center')}>
      <Stepper
        alternativeLabel
        connector={<PhaseConnector />}
        style={{ ['--step-connector-top']: `${connectorTopPx ?? 10}px` } as CSSProperties}
        sx={sxProps ?? {
          '& .MuiStep-root': {
            padding: '0 30px',
          },
        }}>
        {steps?.map((step, index, arr) => {
          const prevRisk = (arr[index - 1]?.risk ?? 'Pending') as StrictRisk;
          const connectorHex = CONNECTOR_HEX_BY_PREV[prevRisk];
          const stepStyle = { ['--connector-color']: connectorHex };
          const isActive = activePhaseKey === step.key;

          return (
            <Step
              key={step.label}
              style={stepStyle as CSSProperties}>
              <StepLabel
                slots={{ stepIcon: PhaseStepIcon }}
                slotProps={{ stepIcon: { step, onStepClick: onClick, classes, active: isActive } as any }}
              />
            </Step>
          );
        })}
      </Stepper>
    </div>
  );
}
